import React from 'react';
import {
    Card,
    Layout,
    Stack,
    Banner,
    Page,
    PageActions,
    TextField
} from '@shopify/polaris';
import { Helmet } from 'react-helmet';
import TagActionComponent from './TagActionComponent';
import { getOperationText } from '../common/Helpers';
import ProductConditionItem from './ProductConditionItem';
import ProductConditionsPaneComponent from './ProductConditionsPaneComponent';

class TagWorkflowCreateComponent extends React.Component {

    nextTagActionId = 0;

    constructor(props) {
        super(props)

        this.state = {
            tagActions: this.props.tagActions,
            productFilters: this.props.productFilters,
            productFiltersLogicalOperator: this.props.productFiltersLogicalOperator,
            errorsTagActions: {},
            description: this.props.description,
            autoUpdateDescription: this.props.autoUpdateDescription,
            tagOperations: this.props.tagOperations
        }

        this.nextTagActionId = this.getLargestId(this.props.tagActions);
    }

    getLargestId = (items) => {
        if (items.length === 0) {
            return 0;
        }

        const max = items.reduce(function (max, item) {
            return Math.max(max, item.id);
        }, 0);
        return max + 1;
    }

    render() {

        let hasActions = this.state.tagActions.length > 0;
        let hasActionErrors = Object.getOwnPropertyNames(this.state.errorsTagActions).length > 0;
        let hasConditionErrors = this.hasConditionErrors(this.state.productFilters);
        let canPreview = hasActions && !hasConditionErrors && !hasActionErrors;

        let actionItems = this.state.tagActions.map((item, index) => {
            return <ProductConditionItem
                number={index + 1}
                error={this.state.errorsTagActions[item.id]}
                onDelete={() => this.handleTagActionDelete(index)}>
                <TagActionComponent
                    action={item}
                    tags={this.props.tags}
                    onActionUpdated={this.handleTagActionUpdated}
                /></ProductConditionItem>
        });

        let tagActionCard = (<Card
            title="TAG ACTIONS"
            footerActionAlignment="left"
            secondaryFooterActionsDisclosureText="Add Tag Action"
            secondaryFooterActions={[
                { content: 'Add tag', onAction: this.onAddTagActionAdd, outline: true },
                { content: 'Add tag conditionally', onAction: this.onAddTagActionConditional, outline: true },
                { content: 'Remove tags', onAction: this.onAddTagActionRemove, outline: true },
                { content: 'Rename tags', onAction: this.onAddTagActionRename, outline: true },
                { content: 'Change prefix', onAction: this.onAddChangePrefix, outline: true },
            ]}
            actions={[
                { content: 'Add Tag', onAction: this.onAddTagActionAdd },
                { content: 'Add tag conditionally', onAction: this.onAddTagActionConditional },
                { content: 'Remove Tags', onAction: this.onAddTagActionRemove },
                { content: 'Rename Tags', onAction: this.onAddTagActionRename },
                { content: 'Change prefix', onAction: this.onAddChangePrefix, outline: true },
            ]}>
            <Card.Section>
                <p>Use tag actions to add or remove tags. Multiple actions can be specified. For example, you can remove multiple tags and add multiple tags at the same time.</p>
            </Card.Section>
            <Card.Section>
                {hasActions &&
                    <Stack vertical spacing="none">
                        {actionItems}
                    </Stack>
                }
                {!hasActions &&
                    <Banner status="critical">
                        <p>At least one tag action must be defined.</p>
                    </Banner>
                }
            </Card.Section>
        </Card>
        );

        let descriptionCard = (<Card
            title="DESCRIPTION"
            footerActionAlignment="left">
            <Card.Section>
                <p>Decription allows you to easily identify an operation. You are not required to fill in this field - we will fill a default value for you.</p>
            </Card.Section>
            <Card.Section>
                <div><TextField placeholder="Operation description" value={this.state.description} onChange={this.handleDescriptionChange} /></div>
            </Card.Section>
        </Card>
        );

        let secondaryActions = [];

        if (this.props.onCancel != null) {
            secondaryActions.push({ content: "Cancel", onAction: this.props.onCancel });
        }

        return (
            <div>
                <Helmet>
                    <title>Tag Genie - Create Tag Operation</title>
                </Helmet>
                <Page
                    title={this.props.title}
                    primaryAction={{ content: this.props.actionText, disabled: !canPreview, onAction: this.onOperationAction }}
                    secondaryActions={secondaryActions}>
                    <Layout>
                        <Layout.Section>
                            <ProductConditionsPaneComponent
                                autoUpdateFilters={true}
                                expanded={true}
                                variants={this.props.variants}
                                smartCollections={this.props.smartCollections}
                                tagOperations={this.props.tagOperations}
                                selectedOptions={this.props.selectedOptions}
                                tags={this.props.tags}
                                productFilters={this.state.productFilters}
                                productFiltersLogicalOperator={this.state.productFiltersLogicalOperator}
                                onFiltersUpdated={this.handleFiltersUpdated}
                            />
                            {tagActionCard}
                            {this.props.showDescription && descriptionCard}
                        </Layout.Section>
                    </Layout>
                    <PageActions
                        primaryAction={{ content: this.props.actionText, disabled: !canPreview, onAction: this.onOperationAction }}
                    />
                </Page>
            </div >

        );
    }

    handleDescriptionChange = (newValue) => {

        this.setState({
            "description": newValue,
            "autoUpdateDescription": false
        });
    }

    hasConditionErrors = (conditions) => {
        const withErrors = conditions.filter(_ => _.isValid === false);

        return withErrors.length > 0;
    }

    handleFiltersUpdated = (newFilters, productFiltersLogicalOperator) => {
        this.setState({
            "productFilters": newFilters,
            "productFiltersLogicalOperator": productFiltersLogicalOperator,
        }, () => this.updateDescription());
    }

    containsSpecialCharacters(str) {
        var regex = /[=[\]'\\,<>?]/g;
        return regex.test(str);
    }

    validateTagActions(tagActions) {
        var errors = {};

        tagActions.forEach((item) => {
            if ((item.type === "add") && (item.tagName === null || item.tagName === "")) {
                errors[item.id] = "Tag name value must be specified";
            }

            if ((item.type === "rename") && (item.tagTemplate === null || item.tagTemplate === "")) {
                errors[item.id] = "Tag name value must be specified";
            }

            if ((item.type === "add" || item.type === "rename" || item.type === "conditionalAdd") && (this.containsSpecialCharacters(item.tagTemplate))) {
                errors[item.id] = "Tag contains characters that are not allowed";
            }

            if ((item.type === "add") && (item.tagName.length > 255)) {
                errors[item.id] = "Tag can be up to 255 characters in lenght";
            }

            if (item.type === "rename" && item.tagTemplate.length > 255) {
                errors[item.id] = "Tag can be up to 255 characters in lenght";
            }

            if (item.type === "conditionalAdd" && item.tagTemplate.length > 255) {
                errors[item.id] = "Tag can be up to 255 characters in lenght";
            }

            if (item.type === "changePrefix") {

                if (item.newPrefix === null) {
                    errors[item.id] = "New prefix value must be specified";
                }

                if (item.originalPrefix === null) {
                    errors[item.id] = "Original prefix value must be specified";
                }

                if (item.keepOriginalTags === null) {
                    errors[item.id] = "KeepOriginalTags value must be specified";
                }
            }

            if (item.filter != null) {
                if (item.filter.value === null || item.filter.value === "") {
                    errors[item.id] = "Tag value must be specified";
                }
            }
        });

        this.setState({
            errorsTagActions: errors
        });
    }

    onAddTagActionAdd = async () => {

        let newAction = this.initNewTagAction({
            type: "add",
            id: this.nextTagActionId++
        })
        let newTagActions = [
            ...this.state.tagActions,
            newAction
        ];

        this.setState({
            tagActions: newTagActions
        }, () => this.updateDescription());

        this.validateTagActions(newTagActions);
    }

    onAddChangePrefix = async () => {

        let newAction = this.initNewTagAction({
            type: "changePrefix",
            id: this.nextTagActionId++
        })
        let newTagActions = [
            ...this.state.tagActions,
            newAction
        ];

        this.setState({
            tagActions: newTagActions
        }, () => this.updateDescription());

        this.validateTagActions(newTagActions);
    }

    onAddTagActionRemove = async () => {

        let newAction = this.initNewTagAction({
            type: "remove",
            id: this.nextTagActionId++
        })
        let newTagActions = [
            ...this.state.tagActions,
            newAction
        ];

        this.setState({
            tagActions: newTagActions
        }, () => this.updateDescription());

        this.validateTagActions(newTagActions);
    }

    onAddTagActionRename = async () => {

        let newAction = this.initNewTagAction({
            type: "rename",
            id: this.nextTagActionId++
        })
        let newTagActions = [
            ...this.state.tagActions,
            newAction
        ];

        this.setState({
            tagActions: newTagActions
        }, () => this.updateDescription());

        this.validateTagActions(newTagActions);
    }

    onAddTagActionConditional = async () => {

        let newAction = this.initNewTagAction({
            type: "conditionalAdd",
            id: this.nextTagActionId++
        })
        let newTagActions = [
            ...this.state.tagActions,
            newAction
        ];

        this.setState({
            tagActions: newTagActions
        }, () => this.updateDescription());

        this.validateTagActions(newTagActions);
    }

    getTagActionById = (id) => {
        var matching = this.state.tagActions.filter((item) => item.id === id);

        if (matching.length > 0) {
            return matching[0];
        }

        return null;
    }

    handleTagActionUpdated = (updatedAction) => {
        let tagAction = this.getTagActionById(updatedAction.id);

        if (tagAction == null) {
            return;
        }

        if (tagAction.type !== updatedAction.type) {
            this.initNewTagAction(updatedAction);
        }

        let index = this.state.tagActions.findIndex(item => item.id === tagAction.id);
        let newTagActions = [...this.state.tagActions];
        newTagActions[index] = updatedAction;
        this.setState({
            tagActions: newTagActions
        }, () => this.updateDescription());

        this.validateTagActions(newTagActions);
    }

    onOperationAction = () => {
        this.props.onAction({
            actions: this.state.tagActions,
            productFilters: this.state.productFilters,
            productFiltersLogicalOperator: this.state.productFiltersLogicalOperator,
            description: this.state.description
        });
    }

    handleTagActionDelete = (index) => {
        let newTagActions = [
            ...this.state.tagActions.slice(0, index),
            ...this.state.tagActions.slice(index + 1)
        ];

        this.setState({
            tagActions: newTagActions
        }, () => this.updateDescription());

        this.validateTagActions(newTagActions);
    }

    updateDescription = () => {
        if (!this.state.autoUpdateDescription) {
            return;
        }

        this.setState({
            "description": getOperationText({
                actions: this.state.tagActions,
                productFilters: this.state.productFilters,
                productFiltersLogicalOperator: this.state.productFiltersLogicalOperator
            }, this.props.smartCollections)
        });
    }

    initNewTagAction = (tagAction) => {

        tagAction.tagName = "";
        tagAction.tagTemplate = "";
        tagAction.filter = null;

        if (tagAction.type === "remove") {
            tagAction.filter = {
                value: null,
                operator: "EqualTo",
                field: "Name"
            };
        }

        if (tagAction.type === "rename") {
            tagAction.filter = {
                value: null,
                operator: "EqualTo",
                field: "Name"
            };
        }

        if (tagAction.type === "conditionalAdd") {
            tagAction.filter = {
                value: null,
                operator: "EqualTo",
                field: "Name"
            };
        }

        if (tagAction.type === "changePrefix") {
            tagAction.originalPrefix = "";
            tagAction.newPrefix = "";
            tagAction.keepOriginalTags = false;
        }

        return tagAction;
    }

    componentDidMount() {
        this.validateTagActions(this.state.tagActions);
    }
}

TagWorkflowCreateComponent.defaultProps = {
    tagActions: [],
    errorsTagActions: {},
    errorsProductFilters: {},
    description: null,
    autoUpdateDescription: true,
    showDescription: false
}

export default TagWorkflowCreateComponent;